/**
 * Maps a raw submission status into one of the high-level display states the
 * submission status screen renders a side panel for.
 *
 * @module submissionDisplayState
 */

/**
 * The distinct visual states of the submission status screen. Each state
 * (except {@link SubmissionDisplayState} `in_progress`) renders its own side
 * panel with "Steps to be Completed" and/or an agent steps tracker.
 */
export type SubmissionDisplayState =
  | "in_progress"
  | "not_submitted"
  | "automation_completed"
  | "submitted_successfully"
  | "fax_delivered"
  | "fax_not_delivered"
  | "draft_saved"
  | "submission_failed"
  | "queued"
  | "payer_not_supported";

/**
 * Resolves the screen's display state from the order's Postgres
 * `submission_status` (a single column carrying both portal and fax statuses).
 *
 * Only values that Postgres actually emits are handled:
 * - `null` / empty means no filing attempt has been recorded → `not_submitted`.
 * - `completed` is the RPA bot's own success value and maps to
 *   `automation_completed`, which still asks the user to verify on the portal
 *   and mark the order as submitted.
 * - `submission_completed` / `already_filed` are the "success" outcomes and
 *   split into the fax-delivered vs portal-success framing via `isFaxSubmitted`.
 * - `error` maps to a single `submission_failed` state for both fax and portal
 *   flows (there is no separate fax-error display).
 * - `payer_not_supported` has its own state (manual-submission guidance); it is
 *   NOT framed as a success.
 * - an unknown non-empty status maps to `in_progress` so an in-flight order is
 *   never rendered as a success.
 *
 * @input status - Raw `submission_status` value from Postgres.
 * @input isFaxSubmitted - Whether the screen is rendering a fax submission flow.
 * @returns The display state used to pick the side panel content.
 */
export const resolveSubmissionDisplayState = (
  status: string | undefined,
  isFaxSubmitted: boolean,
): SubmissionDisplayState => {
  if (!status) {
    return "not_submitted";
  }

  switch (status) {
    case "in_progress":
    case "fax_submitted":
    case "queued":
      return "in_progress";
    case "saved_draft":
      return "draft_saved";
    case "fax_delivered":
      return "fax_delivered";
    case "fax_error":
    case "fax_failed":
      return "fax_not_delivered";
    case "completed":
      return "automation_completed";
    case "submission_completed":
    case "already_filed":
      return isFaxSubmitted ? "fax_delivered" : "submitted_successfully";
    case "payer_not_supported":
      return "payer_not_supported";
    case "error":
      return "submission_failed";
    default:
      return "in_progress";
  }
};

/**
 * Indicates whether a side panel should be rendered for the given display state.
 * The in-progress state intentionally shows no side panel.
 *
 * @input state - The resolved display state.
 * @returns True when a side panel should accompany the main content.
 */
export const hasSidePanel = (state: SubmissionDisplayState): boolean =>
  state !== "in_progress";

/**
 * Indicates whether the status banner should show the "Download Document" /
 * "View Screenshot" actions. These require an artefact produced by an automation
 * run, so they are hidden for `payer_not_supported` / `not_submitted` (no
 * automation ran) and for the in-progress state (no side panel at all).
 *
 * @input state - The resolved display state.
 * @returns True when the document/screenshot actions should be shown.
 */
export const hasDocumentActions = (state: SubmissionDisplayState): boolean =>
  hasSidePanel(state) &&
  state !== "payer_not_supported" &&
  state !== "not_submitted";

/**
 * Indicates whether the footer "Mark as Submitted" action should be shown.
 * Hidden once the order is already in a human-confirmed success outcome
 * (`submitted_successfully` or `fax_delivered`) so the user cannot re-mark an
 * already-submitted order. It stays visible for `automation_completed`: the bot
 * finished filing, but the human confirmation is exactly what is still pending.
 *
 * @input state - The resolved display state.
 * @returns True when the Mark as Submitted button should be shown.
 */
export const canMarkAsSubmitted = (state: SubmissionDisplayState): boolean =>
  state !== "submitted_successfully" && state !== "fax_delivered";
